"use client";

import {
	KeyboardSensor,
	type KeyboardSensorOptions,
	MouseSensor,
	type SensorDescriptor,
	type SensorOptions,
	TouchSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";

/** Desktop: move this many px before drag activates (scroll-safe). */
export const SQ_DND_POINTER_DISTANCE_PX = 8;

/** Touch: press-and-hold before drag (scroll pan wins under delay). */
export const SQ_DND_TOUCH_DELAY_MS = 300;

/** Touch: finger may move this many px during hold without cancelling. */
export const SQ_DND_TOUCH_TOLERANCE_PX = 8;

export interface UseSqDndSensorsOptions {
	/** Override desktop activation distance. Default {@link SQ_DND_POINTER_DISTANCE_PX}. */
	distance?: number;
	/** Override touch press-hold delay. Default {@link SQ_DND_TOUCH_DELAY_MS}. */
	touchDelay?: number;
	/** Override touch hold tolerance. Default {@link SQ_DND_TOUCH_TOLERANCE_PX}. */
	touchTolerance?: number;
	/** Include keyboard sensor (structure / sortable lists). Default true. */
	keyboard?: boolean;
	/** Optional sortable keyboard coordinate getter. */
	keyboardCoordinateGetter?: KeyboardSensorOptions["coordinateGetter"];
}

/**
 * Prefer-over DnD sensor SoT — mouse distance + touch press-hold + optional keyboard.
 * Product code must not call `useSensor(PointerSensor|MouseSensor|TouchSensor)` outside this file.
 */
export function useSqDndSensors(
	options: UseSqDndSensorsOptions = {},
): SensorDescriptor<SensorOptions>[] {
	const distance = options.distance ?? SQ_DND_POINTER_DISTANCE_PX;
	const touchDelay = options.touchDelay ?? SQ_DND_TOUCH_DELAY_MS;
	const touchTolerance = options.touchTolerance ?? SQ_DND_TOUCH_TOLERANCE_PX;
	const keyboard = options.keyboard ?? true;

	const mouse = useSensor(MouseSensor, {
		activationConstraint: { distance },
	});
	const touch = useSensor(TouchSensor, {
		activationConstraint: {
			delay: touchDelay,
			tolerance: touchTolerance,
		},
	});
	const keys = useSensor(
		KeyboardSensor,
		options.keyboardCoordinateGetter
			? { coordinateGetter: options.keyboardCoordinateGetter }
			: undefined,
	);

	return useSensors(mouse, touch, ...(keyboard ? [keys] : []));
}
